import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import './BookingPage.css';

// SECURITY (CAND-010): the meeting link comes from backend/host-supplied data and is
// rendered as a clickable href. Only allow http(s) so a stored javascript:/data: URL
// can't execute when a guest clicks "Join". Returns a safe URL string or null.
const safeHttpUrl = (value) => {
  if (typeof value !== 'string' || !value.trim()) return null;
  try {
    // Parse as an absolute URL (no base): a meeting link is always absolute, and this
    // rejects "" / relative strings instead of resolving them against our own origin.
    const u = new URL(value.trim());
    return (u.protocol === 'http:' || u.protocol === 'https:') ? u.href : null;
  } catch (e) {
    return null;
  }
};

const GuestBookingPage = () => {
  const { guestToken } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);
  const [rescheduleValue, setRescheduleValue] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`/api/calendar/booking/guest/${guestToken}`)
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Booking not found');
        setBooking(data);
        if (data.slot) {
          const d = new Date(data.slot);
          const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
          setRescheduleValue(local);
        }
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || 'Booking not found');
        setLoading(false);
      });
  }, [guestToken]);

  const cancelBooking = async () => {
    if (!window.confirm('Cancel this booking?')) return;
    setCancelling(true);
    try {
      const res = await fetch(`/api/calendar/booking/guest/${guestToken}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'Guest cancelled from app link' })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Cancellation failed');
      setBooking(prev => prev ? { ...prev, status: 'cancelled' } : prev);
    } catch (err) {
      alert(err.message || 'Cancellation failed');
    } finally {
      setCancelling(false);
    }
  };

  const rescheduleBooking = async () => {
    if (!rescheduleValue) return;
    setRescheduling(true);
    try {
      const nextSlot = new Date(rescheduleValue);
      const res = await fetch(`/api/calendar/booking/guest/${guestToken}/reschedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slot: nextSlot.toISOString() })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Reschedule failed');
      setBooking(prev => prev ? { ...prev, slot: data.slot, status: data.status || prev.status } : prev);
      alert(data.confirmation || 'Booking rescheduled');
    } catch (err) {
      alert(err.message || 'Reschedule failed');
    } finally {
      setRescheduling(false);
    }
  };

  if (loading) return <div className="booking-loading">Loading booking...</div>;
  if (error) return <div className="booking-error">{error}</div>;

  const slotDate = new Date(booking.slot);
  const status = (booking.status || '').toLowerCase();

  return (
    <div className="booking-page">
      <div className="booking-container booking-manage-container">
        <div className="booking-host">
          <div className="host-avatar">{(booking.guestName || '?')[0].toUpperCase()}</div>
          <p className="host-name">Manage Booking</p>
          <h1 className="booking-title">{booking.hostUsername}</h1>
          <p className="booking-desc">Review your reservation details or cancel if your plans changed.</p>
          <div className="booking-meta">
            <div className="meta-item">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              {slotDate.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
            </div>
            <div className="meta-item">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              {slotDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} for {booking.duration} min
            </div>
          </div>
        </div>

        <div className="booking-picker">
          <h3>Your Booking</h3>
          <div className={`guest-booking-status ${status}`}>
            {status === 'cancelled' ? 'Cancelled' : 'Confirmed'}
          </div>
          <div className="guest-booking-card">
            <div className="guest-booking-row">
              <span>Booking ID</span>
              <strong>{booking.bookingId}</strong>
            </div>
            <div className="guest-booking-row">
              <span>Guest</span>
              <strong>{booking.guestName}</strong>
            </div>
            <div className="guest-booking-row">
              <span>Host</span>
              <strong>{booking.hostUsername}</strong>
            </div>
            {safeHttpUrl(booking.meetingLink) ? (
              <div className="guest-booking-row">
                <span>Meeting link</span>
                <strong><a href={safeHttpUrl(booking.meetingLink)} target="_blank" rel="noreferrer">Join</a></strong>
              </div>
            ) : null}
          </div>

          {status === 'confirmed' ? (
            <>
              <div className="guest-reschedule-form">
                <label htmlFor="rescheduleAt" className="guest-reschedule-label">Reschedule</label>
                <input
                  id="rescheduleAt"
                  className="guest-reschedule-input"
                  type="datetime-local"
                  value={rescheduleValue}
                  onChange={(e) => setRescheduleValue(e.target.value)}
                />
                <button className="btn-booking-reschedule" onClick={rescheduleBooking} disabled={rescheduling || !rescheduleValue}>
                  {rescheduling ? 'Rescheduling...' : 'Reschedule Booking'}
                </button>
              </div>
              <button className="btn-booking-cancel" onClick={cancelBooking} disabled={cancelling}>
                {cancelling ? 'Cancelling...' : 'Cancel Booking'}
              </button>
            </>
          ) : (
            <p className="guest-booking-note">This booking has already been cancelled.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default GuestBookingPage;
